import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { readClaims, useSession, type AppRole } from '../../hooks/useSession';

type ProtectedRouteProps = {
  children: ReactNode;
  /**
   * One or more roles allowed to access this route. If omitted, any
   * authenticated user is allowed.
   */
  allowedRoles?: AppRole[];
  /**
   * Where to redirect unauthenticated users. Defaults to the admin sign-in page.
   */
  signInPath?: string;
};

/**
 * Gate for authenticated routes. Redirects to `signInPath` when no session
 * exists. If `allowedRoles` is provided, users without a matching role are
 * sent to a `/unauthorized` landing.
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  signInPath = '/admin',
}: ProtectedRouteProps) {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) {
    // Minimal placeholder — theme-agnostic so it works under any layout
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          color: 'var(--text-muted, #6b7280)',
          fontFamily: 'var(--font-body, system-ui)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!session) {
    return <Navigate to={signInPath} state={{ from: location }} replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const { appRole } = readClaims(session);
    if (appRole === null || !allowedRoles.includes(appRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
